import axios, { AxiosError, } from 'axios';
import { EMAIL_VERIFICATION_MESSAGE_KEY, REDIRECT_VERIFY_EMAIL_KEY, } from './constants.js';
import { getMessageFromResponse, attachHumanApiErrorMessage, isAuthFailureStatus, isBackendApplicationErrorEnvelope, isEmailNotVerifiedResponse, isWorkspaceAccessDeniedResponse, } from './errors.js';
function isUnauthorizedEnvelope(data) {
    if (data == null || typeof data !== 'object')
        return false;
    const o = data;
    const status = String(o.status ?? '').toLowerCase();
    if (status !== 'error' && status !== 'fail')
        return false;
    const msg = String(getMessageFromResponse(data) ?? '').toLowerCase();
    if (!msg)
        return false;
    return (msg.includes('unauthorized') ||
        msg.includes('token has been revoked') ||
        msg.includes('id token has been revoked') ||
        msg.includes('token revoked'));
}
/**
 * Creates the shared Axios instance: auth header injection, GET dedupe, 401/403 refresh + retry.
 * Platform-specific behavior is supplied via config callbacks and optional verificationStore.
 */
export function createTroveApiClient(config) {
    const { baseURL, timeoutMs = 15000, getAuthToken, getFreshToken, getActiveAid, onAuthExpired, onEmailNotVerified, onWorkspaceAccessDenied, verificationStore, } = config;
    const api = axios.create({
        baseURL,
        timeout: timeoutMs,
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const pendingRequests = new Map();
    const originalGet = api.get.bind(api);
    api.get = function (url, reqConfig) {
        const cacheKey = `${url}${JSON.stringify(reqConfig?.params || {})}`;
        if (pendingRequests.has(cacheKey)) {
            console.log(`[API] Deduplicating request: ${url}`);
            return pendingRequests.get(cacheKey);
        }
        const promise = originalGet(url, reqConfig).finally(() => {
            setTimeout(() => pendingRequests.delete(cacheKey), 100);
        });
        pendingRequests.set(cacheKey, promise);
        return promise;
    };
    api.interceptors.request.use(async (reqConfig) => {
        const token = await getAuthToken();
        if (token) {
            reqConfig.headers.Authorization = `Bearer ${token}`;
            console.log('[API] Authorization header set for:', reqConfig.url);
        }
        else {
            console.warn('[API] No token available — request will be unauthenticated:', reqConfig.url);
        }
        const aid = getActiveAid?.();
        if (aid) {
            reqConfig.headers['X-Trove-Aid'] = aid;
        }
        return reqConfig;
    }, (error) => Promise.reject(error));
    const notifyEmailNotVerified = async (message) => {
        try {
            await Promise.all([
                verificationStore?.setItem(EMAIL_VERIFICATION_MESSAGE_KEY, message),
                verificationStore?.setItem(REDIRECT_VERIFY_EMAIL_KEY, '1'),
            ]);
        }
        catch {
            // ignore
        }
        onEmailNotVerified?.(message);
    };
    api.interceptors.response.use((response) => {
        if (isUnauthorizedEnvelope(response.data)) {
            const reqConfig = response.config;
            const message = getMessageFromResponse(response.data) ??
                'Unauthorized. The Firebase ID token has been revoked.';
            console.error('[API] Unauthorized auth envelope on HTTP success response', {
                url: reqConfig?.url,
                message,
            });
            onAuthExpired?.({ status: 401, url: reqConfig?.url });
            return Promise.reject(new AxiosError(message, 'ERR_BAD_REQUEST', reqConfig, undefined, {
                ...response,
                status: 401,
                statusText: 'Unauthorized',
            }));
        }
        console.log('[API] Response:', {
            url: response.config?.url,
            status: response.status,
        });
        return response;
    }, async (error) => {
        const status = error.response?.status;
        const data = error.response?.data;
        const reqConfig = error.config;
        const code = error?.code;
        // Replace axios' generic "Request failed with status code NNN" before any early return.
        if (error.response) {
            attachHumanApiErrorMessage(error);
        }
        if (!error.response) {
            console.error('[API] Network/timeout error', {
                code,
                message: error?.message,
                url: reqConfig?.url,
            });
        }
        const defaultEmailNotVerifiedMessage = 'Email id not verified. Please check your email and verify your email address.';
        if (status === 403 && isWorkspaceAccessDeniedResponse(data)) {
            onWorkspaceAccessDenied?.({
                defaultAid: typeof data.defaultAid === 'string' ? data.defaultAid : undefined,
                message: getMessageFromResponse(data),
            });
            return Promise.reject(error);
        }
        if (isAuthFailureStatus(status) && isEmailNotVerifiedResponse(data)) {
            const msgFromBody = getMessageFromResponse(data);
            const message = (typeof msgFromBody === 'string' && msgFromBody) || defaultEmailNotVerifiedMessage;
            // The user may have JUST verified in another tab. Cached ID tokens
            // are signed with `email_verified: false` for up to an hour after
            // the actual verification, which translates to a frustrating
            // dashboard full of "Couldn't load" messages. Force-refresh once
            // and retry: if verification is genuinely complete, the new token
            // claims `email_verified: true` and the request succeeds.
            if (reqConfig && !reqConfig._authRetried) {
                const fresh = await getFreshToken();
                if (fresh.kind === 'token') {
                    console.log('[API] EMAIL_NOT_VERIFIED — retrying once with refreshed token', {
                        url: reqConfig.url,
                    });
                    reqConfig._authRetried = true;
                    reqConfig.headers = reqConfig.headers ?? {};
                    reqConfig.headers.Authorization = `Bearer ${fresh.token}`;
                    return api.request(reqConfig);
                }
            }
            console.warn('[API] Email not verified — keeping session, redirecting to verify flow', {
                url: reqConfig?.url,
            });
            await notifyEmailNotVerified(message);
            return Promise.reject(error);
        }
        const msgFromBody = getMessageFromResponse(data);
        if (isAuthFailureStatus(status) &&
            typeof msgFromBody === 'string' &&
            msgFromBody.toLowerCase().includes('unauthorized')) {
            try {
                await Promise.all([
                    verificationStore?.removeItem(EMAIL_VERIFICATION_MESSAGE_KEY),
                    verificationStore?.removeItem(REDIRECT_VERIFY_EMAIL_KEY),
                ]);
            }
            catch {
                // ignore
            }
        }
        if (isAuthFailureStatus(status) && isBackendApplicationErrorEnvelope(data)) {
            console.warn('[API] Application-level error on auth-status code — rejecting without auth-expired', {
                url: reqConfig?.url,
                message: getMessageFromResponse(data),
            });
            return Promise.reject(error);
        }
        if (isAuthFailureStatus(status) && reqConfig && !reqConfig._authRetried) {
            console.error('[API] Auth failure from backend', { status, url: reqConfig.url });
            const fresh = await getFreshToken();
            if (fresh.kind === 'token') {
                console.log('[API] Retrying request with fresh token', { url: reqConfig.url });
                reqConfig._authRetried = true;
                reqConfig.headers = reqConfig.headers ?? {};
                reqConfig.headers.Authorization = `Bearer ${fresh.token}`;
                return api.request(reqConfig);
            }
            if (fresh.kind === 'transient') {
                console.warn('[API] Token refresh failed (transient). Not signing out; reject without auth-expired.', { url: reqConfig.url, cause: fresh.cause });
                const marked = error;
                marked.transientAuthFailure = true;
                return Promise.reject(marked);
            }
            onAuthExpired?.({ status, url: reqConfig.url });
        }
        attachHumanApiErrorMessage(error);
        return Promise.reject(error);
    });
    return api;
}
